import { useState, useEffect } from 'react';
import { useDataSource } from '@/contexts/DataSourceContext';
import { apiClient } from '@/services/apiClient';

export interface ManagerDashboardSummaryDto {
  currentWeek: {
    start: string;
    end: string;
  };
  teamThisWeek: {
    totalHours: number;
    billableHours: number;
    utilizationRate: number;
    trendPercentage: number;
    teamSize: number;
  };
  approvalsRequired: {
    pendingCount: number;
    overdueCount: number;
    pendingApprovals: PendingApprovalDto[];
  };
  teamPerformance: {
    utilizationRate: number;
    utilizationTarget: number;
    isAboveTarget: boolean;
    teamSize: number;
    trendPercentage: number;
  };
  recentTeamActivity: RecentTeamActivityDto[];
}

export interface RecentTeamActivityDto {
  employeeName: string;
  employeeId: number;
  action: string;
  description: string;
  type: 'submitted' | 'approved' | 'overdue';
  activityDate: string;
  totalHours?: number;
}

export interface PendingApprovalDto {
  timesheetId: number;
  employeeId: string;
  employeeName: string;
  weekEnding: string;
  submittedAt?: string;
  daysSinceSubmission: number;
  isOverdue: boolean;
}

export function useManagerDashboardSummary(screenId: string = 'homepage') {
  const { getScreenMode } = useDataSource();
  const [summary, setSummary] = useState<ManagerDashboardSummaryDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const mode = getScreenMode(screenId);

  useEffect(() => {
    if (mode !== 'live') {
      setLoading(false);
      return;
    }

    const fetchSummary = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const data = await apiClient.get<ManagerDashboardSummaryDto>('/dashboard/manager-summary');
        setSummary(data);
      } catch (err) {
        console.error('Error fetching manager homepage summary:', err);
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, [mode, screenId]);

  // Listen for company-changed event to refetch data when switching companies
  // This ensures data is fetched from the correct integration source (local DB vs external API)
  useEffect(() => {
    const handleCompanyChange = async () => {
      if (mode !== 'live') return;

      try {
        setLoading(true);
        setError(null);
        
        const data = await apiClient.get<ManagerDashboardSummaryDto>('/dashboard/manager-summary');
        setSummary(data);
      } catch (err) {
        console.error('Error fetching manager homepage summary after company change:', err);
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    };

    window.addEventListener('company-changed', handleCompanyChange);
    return () => window.removeEventListener('company-changed', handleCompanyChange);
  }, [mode]);

  return { summary, loading, error, mode };
}
